import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, ListChecks, ShieldAlert } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/data/EmptyState'
import { VersionsTable } from '@/components/tier-policy/VersionsTable'
import { useTierPolicyVersions } from '@/hooks/api/useTierPolicy'
import { useRoles } from '@/hooks/useRoles'
import { ApiError } from '@/lib/api'

export const Route = createFileRoute('/tier-policy/')({
    component: TierPolicyIndex,
})

function TierPolicyIndex() {
    const { hasAnyRole } = useRoles()
    const query = useTierPolicyVersions()

    if (!hasAnyRole('admin', 'mlro', 'read_only')) {
        return (
            <EmptyState
                icon={ShieldAlert}
                title="No access"
                description="Your operator session does not include a role that can view tier policies."
            />
        )
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Tier policy</h1>
                <p className="text-muted-foreground text-sm">
                    Versioned gate + activity-cap definitions. Exactly one version is active at a
                    time; changes roll out by staging a future activation.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Versions</CardTitle>
                    <CardDescription>
                        Click a version to inspect or edit. Rollback appears on superseded versions.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {query.isLoading ? (
                        <Skeleton className="h-40 w-full" />
                    ) : query.isError ? (
                        <EmptyState
                            icon={
                                query.error instanceof ApiError && query.error.status === 401
                                    ? AlertCircle
                                    : ListChecks
                            }
                            title={
                                query.error instanceof ApiError && query.error.status === 401
                                    ? 'Session expired'
                                    : 'Could not load tier policies'
                            }
                            description={
                                query.error instanceof ApiError && query.error.status === 404
                                    ? 'Backend endpoint not available yet.'
                                    : query.error instanceof Error
                                      ? query.error.message
                                      : undefined
                            }
                        />
                    ) : (
                        <VersionsTable versions={query.data?.versions ?? []} />
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
